import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import {
  Button,
  Iconography,
  IconographySizes,
  InlineEditor,
} from "@hid-galaxy-ui/galaxy-react";
import IToolBarConfig from "@hid-galaxy-ui/galaxy-react/components/InlineEditor/IToolbarConfig";
import { sanitizeBenefitDescription } from "./sanitizeBenefitDescription";

type Props = {
  open: boolean;
  editable: boolean;
  title: string;
  value: string;
  onClose: () => void;
  onSave: (value: string) => void;
};

const notesToolBarConfig: IToolBarConfig = {
  headingConfig: {
    isDisplayHeading: true,
    headings: [false, 1, 2, 3, 4, 5, 6],
  },
  fontStyleConfig: {
    isDisplayBold: true,
    isDisplayItalic: true,
    isDisplayUnderline: true,
    isDisplayStrike: true,
  },
  indentConfig: {
    isDisplayOrdered: true,
    isDisplayBullet: true,
    isDisplayIndent: false,
  },
  colorConfig: {
    isDisplayBackground: false,
    isDisplayColor: false,
  },
  alignmentConfig: {
    isDisplayJustify: false,
    isDisplayCenter: false,
    isDisplayRight: false,
  },
  miscConfig: {
    isDisplayImage: false,
    isDisplayLink: false,
    isDisplayCodeblock: false,
    isDisplayBlockquote: false,
  },
  buttonConfig: {
    buttonLabel: "",
    displayPostButton: false,
    isRequired: false,
  },
};

const DescriptionModal: React.FC<Props> = ({
  open,
  editable,
  title,
  value,
  onClose,
  onSave,
}) => {
  const [editorValue, setEditorValue] = useState(value);

  useEffect(() => {
    if (open) {
      setEditorValue(value || "");
    }
  }, [open, value]);

  const sanitizedHtml = sanitizeBenefitDescription(value);

  const handleSave = () => {
    onSave(sanitizeBenefitDescription(editorValue));
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "6px",
          boxShadow: "0px 12px 32px rgba(15, 23, 42, 0.18)",
          maxWidth: 760,
        },
      }}
    >
      <div className="benefits-ptv-modal">
        <header className="benefits-ptv-modal-header">
          <div>
            <p className="benefits-ptv-modal-eyebrow">Partner Program Configuration</p>
            <h3>{editable ? "View & Edit Description" : "View Description"}</h3>
            <p className="benefits-ptv-modal-subtitle">
              Review and manage benefit description
            </p>
          </div>
          <button
            type="button"
            className="benefits-ptv-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <Iconography icon="xmark" size={IconographySizes.Medium} />
          </button>
        </header>
        <DialogContent className="benefits-ptv-modal-content">
          <p className="benefits-description-modal-title">{title}</p>
          {editable ? (
            <InlineEditor
              id="benefits-description-inline-editor"
              placeholder="Add description"
              toolBarConfig={notesToolBarConfig}
              editorData={editorValue}
              handleOnChange={setEditorValue}
            />
          ) : (
            <div
              className="benefits-description-preview"
              dangerouslySetInnerHTML={{
                __html: sanitizedHtml || "<p>No description added.</p>",
              }}
            />
          )}
        </DialogContent>
        <footer className="benefits-ptv-modal-footer">
          <Button variant="secondary" label="CLOSE" onClick={onClose} />
          {editable && (
            <Button variant="primary" label="UPDATE" onClick={handleSave} />
          )}
        </footer>
      </div>
    </Dialog>
  );
};

export default DescriptionModal;
